import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { saveToCache, readFromCache } from "../../../services/pathologyCache";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { FaPrint, FaTrash, FaPlus, FaXRay, FaMoneyBillWave } from "react-icons/fa";
import PageHeading from "../../PageHeading";
import TableSkeleton from "../../TableSkeleton";
import ExportOptions from "../../ExportOptions";
import DynamicSelect from "../../DynamicSelect";
import { NicuPrintLayout } from "../../NicuPrintLayout";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const XRAY_URL = "https://api.muktihospital.com/api/nicu/xray";
const ADMISSION_URL = "https://api.muktihospital.com/api/nicu/admission";

const filterOptions = [
  { value: "monthly", label: "Monthly" },
  { value: "daily", label: "Daily" },
  { value: "yearly", label: "Yearly" },
  { value: "all", label: "All Time" },
];

const patientTypeOptions = [
  { value: "all", label: "All Patients" },
  { value: "NICU", label: "NICU Only" },
  { value: "Outdoor", label: "Outdoor Only" },
];

const statusOptions = [
  { value: "all", label: "All" },
  { value: "paid", label: "Paid" },
  { value: "due", label: "Due" },
];

const numberToWordsBD = (value) => {
  let num = Math.floor(Number(value) || 0);
  if (num === 0) return "Zero";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const two = (n) => (n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : ""));
  const three = (n) => {
    const h = Math.floor(n / 100);
    const r = n % 100;
    return (h ? ones[h] + " Hundred" + (r ? " " : "") : "") + (r ? two(r) : "");
  };
  let words = "";
  const crore = Math.floor(num / 10000000); num %= 10000000;
  const lakh = Math.floor(num / 100000); num %= 100000;
  const thousand = Math.floor(num / 1000); num %= 1000;
  if (crore) words += three(crore) + " Crore ";
  if (lakh) words += two(lakh) + " Lakh ";
  if (thousand) words += two(thousand) + " Thousand ";
  if (num) words += three(num);
  return words.trim();
};

const parseItems = (items) => {
  if (Array.isArray(items)) return items;
  if (typeof items === "string") {
    try { return JSON.parse(items) || []; } catch { return []; }
  }
  return [];
};

const emptyRow = () => ({ typeId: "", name: "", price: 0, qty: 1 });

const emptyForm = () => ({
  patientType: "NICU",
  regNo: "",
  patientName: "",
  phoneNumber: "",
  age: "",
  gender: "",
  referredBy: "",
  technician: "",
  discount: "",
  paidAmount: "",
  xrayDate: new Date().toISOString().split("T")[0],
  note: "",
});

/**
 * One page for the whole X-ray desk: take a new X-ray (for an admitted patient
 * or someone walking in), see what has been done, collect any money still due,
 * and print the receipt.
 */
const NicuXray = () => {
  const [types, setTypes] = useState([]);
  const [admittedPatients, setAdmittedPatients] = useState([]);
  const [xrays, setXrays] = useState([]);
  const [summary, setSummary] = useState({ totalSlips: 0, totalFilms: 0, totalAmount: 0, totalPaid: 0, totalDue: 0 });
  const [todayFilms, setTodayFilms] = useState(0);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [nicuOfflineActive, setNicuOfflineActive] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [form, setForm] = useState(emptyForm());
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [rows, setRows] = useState([emptyRow()]);
  const [discountAuto, setDiscountAuto] = useState(true);

  const [filterType, setFilterType] = useState("monthly");
  const [patientTypeFilter, setPatientTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [payTarget, setPayTarget] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [receipt, setReceipt] = useState(null);

  const itemsPerPage = 10;
  const isNicuPatient = form.patientType === "NICU";

  // ---- Load the price list and the admitted patients ----
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${XRAY_URL}/type`, { headers: { "x-api-key": API_KEY } });
        setTypes(
          (res.data.types || []).map((t) => ({
            value: t.id,
            label: `${t.name} — ৳${t.price}`,
            name: t.name,
            price: t.price,
          }))
        );
      } catch (err) {
        console.error("Failed to load X-ray types:", err);
        toast.error("Failed to load the X-ray price list.");
      }
    })();

    (async () => {
      try {
        const res = await axios.get(ADMISSION_URL, {
          headers: { "x-api-key": API_KEY },
          params: { limit: 500 },
        });
        const list = res.data.data?.admissions || [];
        setAdmittedPatients(
          list
            .filter((a) => a.status === "Admitted")
            .map((a) => ({
              value: a.regNo,
              label: `${a.patientName || "Unknown"} — ${a.regNo || ""}`,
              patientName: a.patientName || "",
              regNo: a.regNo || "",
              phoneNumber: a.phoneNumber || a.relativeMobileNumber || "",
              age: a.age || "",
              gender: a.sex || "",
              consultantName: a.consultantName || "",
            }))
        );
      } catch (err) {
        console.error("Failed to load admitted patients:", err);
      }
    })();
  }, []);

  // ---- Load the list for the current filter ----
  const fetchXrays = useCallback(async () => {
    setLoading(true);
    const params = { page: currentPage, limit: itemsPerPage };
    if (filterType === "daily") { params.startDate = startDate; params.endDate = endDate; }
    else if (filterType === "monthly") { const [y, m] = selectedMonth.split("-"); params.year = y; params.month = m; }
    else if (filterType === "yearly") { params.year = selectedYear; }
    if (patientTypeFilter !== "all") params.patientType = patientTypeFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    if (searchQuery) params.search = searchQuery;

    const cacheKey = `nicu_xrays_${JSON.stringify(params)}`;

    try {
      const res = await axios.get(XRAY_URL, { headers: { "x-api-key": API_KEY }, params });
      setXrays(res.data.xrays || []);
      setSummary(res.data.summary || { totalSlips: 0, totalFilms: 0, totalAmount: 0, totalPaid: 0, totalDue: 0 });
      setPagination(res.data.pagination || { total: 0, page: 1, totalPages: 1 });

      // Cache this filter for offline viewing.
      setNicuOfflineActive(false);
      saveToCache(cacheKey, {
        xrays: res.data.xrays || [],
        summary: res.data.summary || { totalSlips: 0, totalFilms: 0, totalAmount: 0, totalPaid: 0, totalDue: 0 },
        pagination: res.data.pagination || { total: 0, page: 1, totalPages: 1 },
      });
    } catch (err) {
      console.error("Error loading X-rays:", err);
      if (err.code === "ERR_NETWORK" || !navigator.onLine) {
        const cached = await readFromCache(cacheKey);
        if (cached && cached.xrays) {
          setXrays(cached.xrays);
          setSummary(cached.summary || { totalSlips: 0, totalFilms: 0, totalAmount: 0, totalPaid: 0, totalDue: 0 });
          setPagination(cached.pagination || { total: 0, page: 1, totalPages: 1 });
          setNicuOfflineActive(true);
          toast.info("Offline: showing saved X-ray records.");
        } else {
          setXrays([]);
          setNicuOfflineActive(false);
          toast.info("Offline: this filter isn't saved yet. Open it online once.");
        }
      } else {
        toast.error("Failed to load the X-ray records.");
        setXrays([]);
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterType, patientTypeFilter, statusFilter, startDate, endDate, selectedMonth, selectedYear, searchQuery]);

  useEffect(() => { fetchXrays(); }, [fetchXrays]);

  // Today's count is asked for every day, so it is fetched on its own and does
  // not move when the list filter changes.
  const fetchToday = useCallback(async () => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const res = await axios.get(XRAY_URL, {
        headers: { "x-api-key": API_KEY },
        params: { startDate: today, endDate: today, limit: 1 },
      });
      setTodayFilms(res.data.summary?.totalFilms || 0);
    } catch (err) {
      console.error("Failed to load today's count:", err);
    }
  }, []);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  // ---- Print once the receipt data is ready ----
  useEffect(() => {
    if (!receipt) return;
    const timer = setTimeout(() => {
      window.print();
      setTimeout(() => setReceipt(null), 500);
    }, 400);
    return () => clearTimeout(timer);
  }, [receipt]);

  // ---- Form handlers ----
  const validRows = rows.filter((r) => r.typeId && r.name);
  const totalAmount = validRows.reduce((s, r) => s + (Number(r.price) || 0) * (parseInt(r.qty) || 1), 0);
  const discountAmount = Math.round(parseFloat(form.discount) || 0);
  const netAmount = Math.max(0, totalAmount - discountAmount);
  const paidNow = Math.min(Math.round(parseFloat(form.paidAmount) || 0), netAmount);
  const dueNow = netAmount - paidNow;

  useEffect(() => {
    if (!discountAuto) return;
    if (form.paidAmount === "" || form.paidAmount === null) return;
    const paid = Math.round(parseFloat(form.paidAmount) || 0);
    const nextDiscount = String(Math.max(0, totalAmount - paid));
    setForm((prev) => (prev.discount === nextDiscount ? prev : { ...prev, discount: nextDiscount }));
  }, [totalAmount, form.paidAmount, discountAuto]);

  const handleRowChange = (index, option) => {
    if (option && rows.some((r, i) => i !== index && r.typeId === option.value)) {
      toast.error("This X-ray is already on the slip. Increase its quantity instead.");
      return;
    }
    const next = [...rows];
    next[index] = option
      ? { typeId: option.value, name: option.name, price: Number(option.price) || 0, qty: next[index].qty || 1 }
      : emptyRow();
    setRows(next);
  };

  const handleQtyChange = (index, value) => {
    const next = [...rows];
    next[index] = { ...next[index], qty: value };
    setRows(next);
  };

  const addRow = () => setRows([...rows, emptyRow()]);

  const removeRow = (index) => {
    if (rows.length === 1) { setRows([emptyRow()]); return; }
    setRows(rows.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setForm(emptyForm());
    setSelectedPatient(null);
    setRows([emptyRow()]);
    setDiscountAuto(true);
  };

  const switchPatientType = (type) => {
    setForm({ ...emptyForm(), patientType: type });
    setSelectedPatient(null);
    setRows([emptyRow()]);
    setDiscountAuto(true);
  };

  const handleSave = async (thenPrint) => {
    if (validRows.length === 0) { toast.error("Select at least one X-ray."); return; }
    if (isNicuPatient && !form.regNo) { toast.error("Select an admitted patient first."); return; }
    if (!isNicuPatient && !form.patientName.trim()) { toast.error("Enter the patient's name."); return; }

    try {
      setSaving(true);
      const payload = {
        patientType: form.patientType,
        regNo: isNicuPatient ? form.regNo : null,
        patientName: form.patientName.trim(),
        phoneNumber: form.phoneNumber.trim() || null,
        age: form.age || null,
        gender: form.gender || null,
        referredBy: form.referredBy || null,
        technician: form.technician || null,
        items: validRows.map((r) => ({
          typeId: r.typeId,
          name: r.name,
          price: Number(r.price) || 0,
          qty: parseInt(r.qty) || 1,
        })),
        discount: discountAmount,
        paidAmount: paidNow,
        xrayDate: form.xrayDate,
        note: form.note || null,
      };

      const res = await axios.post(`${XRAY_URL}/add`, payload, { headers: { "x-api-key": API_KEY } });
      const saved = res.data.xray || res.data;
      toast.success(`X-ray slip ${saved.slipNumber} created.`);
      resetForm();
      fetchXrays();
      fetchToday();
      if (thenPrint) setReceipt(saved);
    } catch (err) {
      console.error("Failed to save the X-ray:", err);
      toast.error(err.response?.data?.error || "Failed to save the X-ray.");
    } finally {
      setSaving(false);
    }
  };

  const handlePay = async () => {
    if (!payTarget) return;
    const amount = parseFloat(payAmount) || 0;
    if (amount <= 0) { toast.error("Enter an amount greater than 0."); return; }

    try {
      await axios.post(`${XRAY_URL}/pay/${payTarget.id}`, { amount }, { headers: { "x-api-key": API_KEY } });
      toast.success(`৳${Math.round(amount)} received.`);
      setPayTarget(null);
      setPayAmount("");
      fetchXrays();
    } catch (err) {
      console.error("Payment failed:", err);
      toast.error(err.response?.data?.error || "Failed to receive the payment.");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${XRAY_URL}/delete/${deleteTarget.id}`, { headers: { "x-api-key": API_KEY } });
      toast.success("X-ray record deleted.");
      setDeleteTarget(null);
      fetchXrays();
      fetchToday();
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error(err.response?.data?.error || "Failed to delete.");
      setDeleteTarget(null);
    }
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "-");

  const headers = ["Slip No", "Date", "Type", "Reg No", "Patient", "X-rays", "Net", "Paid", "Due"];
  const dataMapper = (x) => {
    const items = parseItems(x.items);
    return [
      x.slipNumber,
      fmtDate(x.xrayDate),
      x.patientType,
      x.regNo || "-",
      x.patientName,
      items.map((i) => `${i.name}${(i.qty || 1) > 1 ? ` x${i.qty}` : ""}`).join(", "),
      x.netAmount,
      x.paidAmount,
      x.dueAmount,
    ].join("\t");
  };

  const breadcrumbs = useMemo(
    () => [{ label: "Dashboard", url: "/" }, { label: "NICU" }, { label: "X-ray" }],
    []
  );

  const roundButton = "size-7 rounded-full text-sm inline-flex items-center justify-center transition-all duration-300";
  const rangeLabel = filterType === "daily" ? "In range" : filterType === "monthly" ? "This month" : filterType === "yearly" ? "This year" : "All time";

  return (
    <div>
      <ToastContainer />
      <div className="print-hidden"><PageHeading title="NICU X-ray" breadcrumbs={breadcrumbs} /></div>
      {nicuOfflineActive && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-orange-50 border border-orange-200 text-orange-800 text-sm flex items-center gap-2 print-hidden">
          <span>⚠️</span>
          <span>Offline mode — showing saved data. Connect to the internet for the latest.</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4 print-hidden">
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-M-primary-color">
          <p className="text-xs text-gray-500">X-rays Today</p>
          <p className="text-xl font-black text-M-primary-color">{todayFilms}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-500">
          <p className="text-xs text-gray-500">X-rays ({rangeLabel})</p>
          <p className="text-xl font-black text-blue-600">{summary.totalFilms}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-gray-400">
          <p className="text-xs text-gray-500">Slips</p>
          <p className="text-xl font-black text-gray-700">{summary.totalSlips}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-[#73CB64]">
          <p className="text-xs text-gray-500">Collected</p>
          <p className="text-xl font-black text-[#73CB64]">৳{Math.round(summary.totalPaid)}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-red-500">
          <p className="text-xs text-gray-500">Due</p>
          <p className="text-xl font-black text-red-600">৳{Math.round(summary.totalDue)}</p>
        </div>
      </div>

      {/* New X-ray */}
      <div className="bg-white shadow-sm rounded-lg mb-4 print-hidden border-l-4 border-[#73CB64]">
        <div className="p-4 border-b border-dashed border-M-text-color/50 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <FaXRay className="text-[#73CB64]" />
            <h2 className="text-base font-medium text-gray-700">New X-ray</h2>
          </div>
          <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
            <button
              onClick={() => switchPatientType("NICU")}
              className={`text-sm py-1.5 px-4 transition-all duration-300 ${
                isNicuPatient ? "bg-M-primary-color text-white" : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              NICU Patient
            </button>
            <button
              onClick={() => switchPatientType("Outdoor")}
              className={`text-sm py-1.5 px-4 transition-all duration-300 ${
                !isNicuPatient ? "bg-M-primary-color text-white" : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              Outdoor Patient
            </button>
          </div>
        </div>

        <div className="p-4">
          {/* Patient details */}
          {isNicuPatient ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
              <div className="md:col-span-2">
                <label className="text-xs font-bold block mb-1">Admitted Patient *</label>
                <DynamicSelect
                  options={admittedPatients}
                  value={selectedPatient}
                  onChange={(opt) => {
                    setSelectedPatient(opt);
                    setForm((prev) => ({
                      ...prev,
                      regNo: opt?.regNo || "",
                      patientName: opt?.patientName || "",
                      phoneNumber: opt?.phoneNumber || "",
                      age: opt?.age || "",
                      gender: opt?.gender || "",
                      referredBy: opt?.consultantName || prev.referredBy,
                    }));
                  }}
                  isSearchable
                  isClearable
                  placeholder="Search patient or Reg No"
                />
                {form.regNo && <p className="text-xs text-gray-400 mt-1">Reg No: {form.regNo}</p>}
              </div>
              <div>
                <label className="text-xs font-bold block mb-1">Phone</label>
                <input type="text" value={form.phoneNumber} readOnly placeholder="Auto from admission"
                  className="w-full border p-2 rounded text-sm bg-gray-100 outline-none" />
              </div>
              <div>
                <label className="text-xs font-bold block mb-1">Age / Sex</label>
                <input type="text" value={`${form.age || "--"} / ${form.gender || "--"}`} readOnly
                  className="w-full border p-2 rounded text-sm bg-gray-100 outline-none" />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
              <div>
                <label className="text-xs font-bold block mb-1">Patient Name *</label>
                <input type="text" placeholder="Full name" value={form.patientName}
                  onChange={(e) => setForm({ ...form, patientName: e.target.value })}
                  className="w-full border p-2 rounded text-sm focus:border-[#73CB64] outline-none" />
              </div>
              <div>
                <label className="text-xs font-bold block mb-1">Phone</label>
                <input type="text" placeholder="01XXXXXXXXX" value={form.phoneNumber}
                  onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                  className="w-full border p-2 rounded text-sm focus:border-[#73CB64] outline-none" />
              </div>
              <div>
                <label className="text-xs font-bold block mb-1">Age</label>
                <input type="text" placeholder="e.g. 25 Y" value={form.age}
                  onChange={(e) => setForm({ ...form, age: e.target.value })}
                  className="w-full border p-2 rounded text-sm focus:border-[#73CB64] outline-none" />
              </div>
              <div>
                <label className="text-xs font-bold block mb-1">Sex</label>
                <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}
                  className="w-full border p-2 rounded text-sm outline-none">
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
            </div>
          )}

          {/* X-ray rows */}
          <div className="space-y-2">
            {rows.map((row, index) => (
              <div key={index} className="flex gap-2 items-center">
                <div className="flex-1">
                  <DynamicSelect
                    options={types.filter(
                      (opt) => opt.value === row.typeId || !rows.some((r, i) => i !== index && r.typeId === opt.value)
                    )}
                    value={row.typeId ? { value: row.typeId, label: `${row.name} — ৳${row.price}` } : null}
                    onChange={(opt) => handleRowChange(index, opt)}
                    isSearchable
                    placeholder="Select an X-ray"
                  />
                </div>
                <input
                  type="number"
                  value={row.qty}
                  onChange={(e) => handleQtyChange(index, e.target.value)}
                  className="w-16 border p-2 rounded text-sm text-center outline-none focus:border-[#73CB64]"
                  min="1"
                  title="Quantity"
                />
                <div className="w-24 text-right text-sm font-medium text-gray-600">
                  ৳{(Number(row.price) || 0) * (parseInt(row.qty) || 1)}
                </div>
                <button
                  onClick={() => removeRow(index)}
                  className={`${roundButton} bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white shrink-0`}
                  title="Remove"
                >
                  <FaTrash size={11} />
                </button>
              </div>
            ))}
          </div>

          <button
            onClick={addRow}
            className="mt-2 text-sm text-M-primary-color hover:underline flex items-center gap-1 font-medium"
          >
            <FaPlus size={12} /> Add another X-ray
          </button>

          {/* Money and extras */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-dashed">
            <div>
              <label className="text-xs font-bold block mb-1">Date</label>
              <input type="date" value={form.xrayDate}
                onChange={(e) => setForm({ ...form, xrayDate: e.target.value })}
                className="w-full border p-2 rounded text-sm outline-none" />
            </div>
            <div>
              <label className="text-xs font-bold block mb-1">Referred By</label>
              <input type="text" placeholder="Doctor name" value={form.referredBy}
                onChange={(e) => setForm({ ...form, referredBy: e.target.value })}
                className="w-full border p-2 rounded text-sm focus:border-[#73CB64] outline-none" />
            </div>
            <div>
              <label className="text-xs font-bold block mb-1">Technician</label>
              <input type="text" placeholder="Who did the X-ray" value={form.technician}
                onChange={(e) => setForm({ ...form, technician: e.target.value })}
                className="w-full border p-2 rounded text-sm focus:border-[#73CB64] outline-none" />
            </div>
            <div>
              <label className="text-xs font-bold block mb-1">Note</label>
              <input type="text" placeholder="Optional" value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="w-full border p-2 rounded text-sm focus:border-[#73CB64] outline-none" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold block mb-1">
                  Discount (৳)
                  {discountAuto && form.paidAmount !== "" && (
                    <span className="ml-1 font-normal text-[10px] text-gray-400">auto</span>
                  )}
                </label>
                <input type="number" placeholder="0" value={form.discount}
                  onChange={(e) => { setDiscountAuto(false); setForm({ ...form, discount: e.target.value }); }}
                  className="w-full border p-2 rounded text-sm text-right focus:border-[#73CB64] outline-none" min="0" />
              </div>
              <div>
                <label className="text-xs font-bold block mb-1">Paid Now (৳)</label>
                <input
                  type="number"
                  placeholder="0"
                  value={form.paidAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    const paid = Math.round(parseFloat(value) || 0);
                    setDiscountAuto(true);
                    setForm((prev) => ({
                      ...prev,
                      paidAmount: value,
                      discount: value === "" ? "" : String(Math.max(0, totalAmount - paid)),
                    }));
                  }}
                  className="w-full border p-2 rounded text-sm text-right focus:border-[#73CB64] outline-none"
                  min="0"
                />
              </div>
            </div>
            <div className="bg-gray-50 rounded-md p-3 text-sm">
              <div className="flex justify-between py-0.5"><span className="text-gray-500">Total</span><span>৳{totalAmount.toFixed(2)}</span></div>
              <div className="flex justify-between py-0.5"><span className="text-gray-500">Discount</span><span>৳{discountAmount.toFixed(2)}</span></div>
              <div className="flex justify-between py-0.5 font-bold border-t mt-1 pt-1"><span>Net Amount</span><span>৳{netAmount.toFixed(2)}</span></div>
              <div className="flex justify-between py-0.5"><span className="text-gray-500">Paid</span><span>৳{paidNow.toFixed(2)}</span></div>
              <div className={`flex justify-between py-0.5 font-bold ${dueNow > 0 ? "text-red-600" : "text-[#73CB64]"}`}>
                <span>Due</span><span>৳{dueNow.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <button onClick={resetForm} disabled={saving}
              className="bg-gray-200 text-gray-700 text-sm py-2 px-5 rounded-md hover:bg-gray-300 disabled:opacity-50 transition-all duration-300">
              Reset
            </button>
            <button onClick={() => handleSave(false)} disabled={saving || validRows.length === 0}
              className="bg-gray-600 text-white text-sm py-2 px-5 rounded-md hover:bg-gray-700 disabled:opacity-50 transition-all duration-300">
              {saving ? "Saving..." : "Save X-ray"}
            </button>
            <button onClick={() => handleSave(true)} disabled={saving || validRows.length === 0}
              className="bg-[#73CB64] text-white text-sm py-2 px-5 rounded-md hover:bg-M-primary-color flex items-center gap-1.5 disabled:opacity-50 transition-all duration-300">
              <FaPrint size={12} /> Save &amp; Print
            </button>
          </div>
        </div>
      </div>

      {/* Records */}
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <h2 className="text-base font-medium text-gray-700">X-ray Records</h2>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="w-32">
              <DynamicSelect options={filterOptions} value={filterOptions.find((o) => o.value === filterType)}
                onChange={(o) => { setFilterType(o.value); setCurrentPage(1); }} isSearchable={false} placeholder="Filter" />
            </div>
            <div className="w-36">
              <DynamicSelect options={patientTypeOptions} value={patientTypeOptions.find((o) => o.value === patientTypeFilter)}
                onChange={(o) => { setPatientTypeFilter(o.value); setCurrentPage(1); }} isSearchable={false} placeholder="Patient" />
            </div>
            <div className="w-24">
              <DynamicSelect options={statusOptions} value={statusOptions.find((o) => o.value === statusFilter)}
                onChange={(o) => { setStatusFilter(o.value); setCurrentPage(1); }} isSearchable={false} placeholder="Status" />
            </div>
            {filterType === "daily" && (
              <>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="p-2 border rounded-md text-sm w-32" />
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="p-2 border rounded-md text-sm w-32" />
              </>
            )}
            {filterType === "monthly" && (
              <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="p-2 border rounded-md text-sm" />
            )}
            {filterType === "yearly" && (
              <input type="number" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="p-2 border rounded-md text-sm w-24" min="2000" max="2100" />
            )}
            <input type="text" placeholder="Search slip, name or Reg No..." value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              className="max-w-48 w-full p-2 border rounded-md text-sm outline-none" />
            <ExportOptions data={xrays} dropdownOpen={dropdownOpen} toggleDropdown={() => setDropdownOpen(!dropdownOpen)}
              title="NICU X-ray Records" headers={headers} dataMapper={dataMapper} />
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <table className="min-w-[1100px] w-full">
              <TableSkeleton rowCount={itemsPerPage}
                columns={[{ width: "110px" }, { width: "90px" }, { width: "80px" }, { width: "90px" }, { width: "150px" }, { width: "180px" }, { width: "80px" }, { width: "80px" }, { width: "110px" }]} />
            </table>
          ) : xrays.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No X-ray found for this filter.</div>
          ) : (
            <table className="min-w-[1100px] w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Slip No</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Date</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Type</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Patient</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">X-rays</th>
                  <th className="p-3 text-right text-sm font-medium text-gray-700">Net (৳)</th>
                  <th className="p-3 text-center text-sm font-medium text-gray-700">Status</th>
                  <th className="p-3 text-center text-sm font-medium text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {xrays.map((x) => {
                  const items = parseItems(x.items);
                  const films = items.reduce((s, i) => s + (parseInt(i.qty) || 1), 0);
                  const isPaid = (x.dueAmount || 0) <= 0;
                  const locked = (x.paidAmount || 0) > 0;
                  return (
                    <tr key={x.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 text-sm font-medium text-gray-700">{x.slipNumber}</td>
                      <td className="p-3 text-sm text-gray-500 whitespace-nowrap">{fmtDate(x.xrayDate)}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          x.patientType === "NICU" ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"
                        }`}>
                          {x.patientType}
                        </span>
                      </td>
                      <td className="p-3 text-sm text-gray-700">
                        <div className="flex flex-col">
                          <span>{x.patientName}</span>
                          <span className="text-[11px] text-gray-400">{x.regNo || x.phoneNumber || ""}</span>
                        </div>
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        <span title={items.map((i) => `${i.name}${(i.qty || 1) > 1 ? ` x${i.qty}` : ""}`).join(", ")}>
                          {films} film{films !== 1 ? "s" : ""}
                        </span>
                      </td>
                      <td className="p-3 text-sm text-right font-bold text-M-primary-color">৳{x.netAmount}</td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          isPaid ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
                        }`}>
                          {isPaid ? "PAID" : `DUE ৳${x.dueAmount}`}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => setReceipt(x)}
                            className={`${roundButton} bg-M-primary-color/10 text-M-primary-color hover:bg-M-primary-color hover:text-white`}
                            title="Print receipt">
                            <FaPrint size={12} />
                          </button>
                          <button
                            onClick={() => { setPayTarget(x); setPayAmount(String(x.dueAmount || 0)); }}
                            disabled={isPaid}
                            className={`${roundButton} ${
                              isPaid ? "bg-gray-100 text-gray-300 cursor-not-allowed" : "bg-[#73CB64]/10 text-[#73CB64] hover:bg-[#73CB64] hover:text-white"
                            }`}
                            title={isPaid ? "Nothing due" : "Receive payment"}
                          >
                            <FaMoneyBillWave size={12} />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(x)}
                            disabled={locked}
                            className={`${roundButton} ${
                              locked ? "bg-gray-100 text-gray-300 cursor-not-allowed" : "bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white"
                            }`}
                            title={locked ? "Money has been taken — cannot delete" : "Delete this record"}
                          >
                            <FaTrash size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {pagination.totalPages > 1 && (
          <div className="p-4 flex justify-between items-center print-hidden">
            <p className="text-sm text-gray-600">
              Records: {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, pagination.total)} of {pagination.total}
            </p>
            <div className="flex gap-2">
              <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}
                className="p-2 border rounded disabled:opacity-50"><MdKeyboardArrowLeft /></button>
              <span className="p-2 text-sm">Page {currentPage} of {pagination.totalPages}</span>
              <button disabled={currentPage === pagination.totalPages} onClick={() => setCurrentPage((p) => p + 1)}
                className="p-2 border rounded disabled:opacity-50"><MdKeyboardArrowRight /></button>
            </div>
          </div>
        )}
      </div>

      {/* Receive payment */}
      {payTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-5">
            <h3 className="text-base font-bold text-gray-700 mb-1">Receive payment</h3>
            <p className="text-sm text-gray-500 mb-3">
              {payTarget.slipNumber} — {payTarget.patientName}. Due ৳{payTarget.dueAmount}.
            </p>
            <label className="text-xs font-bold block mb-1">Amount (৳)</label>
            <input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)}
              className="w-full border p-2 rounded text-sm mb-4 text-right focus:border-[#73CB64] outline-none" min="0" />
            <div className="flex justify-end gap-3">
              <button onClick={() => { setPayTarget(null); setPayAmount(""); }}
                className="bg-gray-200 text-gray-700 text-sm py-2 px-4 rounded-md hover:bg-gray-300 transition-all duration-300">
                Cancel
              </button>
              <button onClick={handlePay}
                className="bg-[#73CB64] text-white text-sm py-2 px-4 rounded-md hover:bg-M-primary-color transition-all duration-300">
                Receive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-5">
            <h3 className="text-base font-bold text-gray-700 mb-2">Delete {deleteTarget.slipNumber}?</h3>
            <p className="text-sm text-gray-500 mb-4">This removes the record from the X-ray count as well.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteTarget(null)}
                className="bg-gray-200 text-gray-700 text-sm py-2 px-4 rounded-md hover:bg-gray-300 transition-all duration-300">
                Cancel
              </button>
              <button onClick={handleDelete}
                className="bg-M-secondary-color text-white text-sm py-2 px-4 rounded-md hover:bg-red-700 transition-all duration-300">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Printable receipt ===== */}
      {receipt && (
        <NicuPrintLayout
          className="xray-receipt"
          title="X-ray Receipt"
          compact
          isPaid={(receipt.dueAmount || 0) <= 0}
          amountInWords={`${numberToWordsBD(receipt.netAmount)} Taka Only`}
          signatures={["Prepared By", "Authorized By", "Received By"]}
          billInfo={
            <div style={{ border: "1px solid #000", padding: "5px 8px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", columnGap: "14px", rowGap: "1px" }}>
                <p style={{ margin: 0 }}><strong>Slip No:</strong> {receipt.slipNumber}</p>
                <p style={{ margin: 0 }}><strong>Date:</strong> {fmtDate(receipt.xrayDate)}</p>
                <p style={{ margin: 0 }}><strong>Type:</strong> {receipt.patientType}</p>

                <p style={{ margin: 0 }}><strong>Patient:</strong> {receipt.patientName}</p>
                <p style={{ margin: 0 }}><strong>Age / Sex:</strong> {receipt.age || "--"} / {receipt.gender || "--"}</p>
                <p style={{ margin: 0 }}><strong>{receipt.regNo ? "Reg No" : "Phone"}:</strong> {receipt.regNo || receipt.phoneNumber || "N/A"}</p>

                <p style={{ margin: 0, gridColumn: "1 / -1" }}>
                  <strong>Referred By:</strong> {receipt.referredBy || "N/A"}
                  {receipt.technician ? `   |   Technician: ${receipt.technician}` : ""}
                </p>
              </div>
            </div>
          }
          table={
            <>
              <thead>
                <tr>
                  <th style={{ width: "8%" }}>Sl</th>
                  <th>X-ray Name</th>
                  <th style={{ width: "14%" }}>Qty</th>
                  <th style={{ width: "22%" }}>Amount (৳)</th>
                </tr>
              </thead>
              <tbody>
                {parseItems(receipt.items).map((item, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{item.name}</td>
                    <td>{item.qty || 1}</td>
                    <td>{((Number(item.price) || 0) * (parseInt(item.qty) || 1)).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </>
          }
          summary={
            <>
              <div><span>Total</span><span>৳ {Number(receipt.totalAmount || 0).toFixed(2)}</span></div>
              <div><span>Discount</span><span>৳ {Number(receipt.discount || 0).toFixed(2)}</span></div>
              <div><span>Net Amount</span><span>৳ {Number(receipt.netAmount || 0).toFixed(2)}</span></div>
              <div><span>Paid</span><span>৳ {Number(receipt.paidAmount || 0).toFixed(2)}</span></div>
              {(receipt.dueAmount || 0) > 0 && (
                <div><span>Due</span><span>৳ {Number(receipt.dueAmount).toFixed(2)}</span></div>
              )}
            </>
          }
          note={
            receipt.note ? (
              <div><strong>Note:</strong> {receipt.note}</div>
            ) : (receipt.dueAmount || 0) > 0 ? (
              <div>Please clear the due amount at the NICU counter.</div>
            ) : null
          }
        />
      )}
    </div>
  );
};

export default NicuXray;
